'use client';

import React, { useState } from 'react';
import { Search, Menu, ChevronDown, School, User, LogOut, Settings } from 'lucide-react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import NotificationWidget from '@/components/yayasan/layout/NotificationWidget';

interface HeaderProps {
    onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
    const pathname = usePathname();
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    // Per Kepala Sekolah hanya memiliki 1 sekolah
    const schoolName = "PAUD Al-Azhar 1";
    const jenjang = "PAUD";

    const getRoleInfo = () => {
        if (pathname.includes('/sekolah/dashboard-guru') || pathname.includes('/sekolah/guru')) {
            return { title: 'Dashboard Guru', role: 'Guru Pengajar', name: 'Siti Aminah', initial: 'S' };
        }
        if (pathname.includes('/sekolah/dashboard-staff') || pathname.includes('/sekolah/staff/inventaris') || pathname.includes('/sekolah/staff/slip-gaji')) {
            return { title: 'Dashboard Staff', role: 'Staff Administrasi', name: 'Andi Pratama', initial: 'A' };
        }
        return { title: 'Dashboard Admin', role: `Kepala Sekolah ${jenjang}`, name: 'Budi Santoso', initial: 'B' };
    };

    const roleInfo = getRoleInfo();

    return (
        <header className="h-24 bg-background/60 backdrop-blur-2xl px-6 md:px-10 py-5 border-b border-border-main flex items-center justify-between sticky top-0 z-40 font-sans shadow-xs">
            {/* Left: Branding */}
            <div className="flex items-center gap-8">
                <button onClick={onMenuClick} className="lg:hidden p-2 -ml-2 text-secondary hover:text-primary transition-all bg-primary-soft/50 rounded-xl">
                    <Menu className="w-6 h-6" />
                </button>

                <div className="flex items-center gap-6">
                    <div className="flex flex-col">
                        <span className="text-[9px] font-black text-muted uppercase tracking-widest leading-none mb-1">Unit Aktif</span>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-success"></div>
                            <span className="text-sm font-bold text-foreground">{schoolName}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right: Actions, Notifications & User */}
            <div className="flex items-center gap-6">
                {/* Global Search Bar */}
                <div className="hidden xl:flex relative group mr-4">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                    <input
                        type="text"
                        placeholder="Cari transaksi atau data siswa..."
                        className="bg-surface/50 border border-border-main rounded-2xl py-2.5 pl-11 pr-5 text-xs font-medium focus:ring-4 focus:ring-primary/5 focus:border-primary/30 transition-all outline-none w-64 lg:w-80"
                    />
                </div>

                <div className="flex items-center gap-3">
                    <NotificationWidget />
                </div>

                <div className="h-10 w-px bg-border-main mx-2"></div>

                {/* User Profile Area */}
                <div className="relative">
                    <button
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center gap-4 focus:outline-none group/profile p-1.5 pr-4 rounded-3xl hover:bg-surface border border-transparent hover:border-border-main transition-all shadow-none hover:shadow-xl hover:shadow-primary/5"
                    >
                        <div className="relative">
                            <div className="w-12 h-12 rounded-[1.25rem] bg-primary flex items-center justify-center text-white font-black text-xl shadow-lg shadow-primary/20 transition-transform group-hover/profile:scale-105 border-2 border-surface">
                                {roleInfo.initial}
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-success border-4 border-surface rounded-full shadow-sm"></div>
                        </div>
                        <div className="hidden lg:flex flex-col items-start pr-2">
                            <p className="text-sm font-bold text-foreground tracking-tight group-hover/profile:text-primary transition-colors">{roleInfo.name}</p>
                            <p className="text-[10px] text-primary font-bold uppercase tracking-widest mt-0.5 opacity-80">{roleInfo.role}</p>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-muted transition-transform group-hover/profile:text-primary ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Profile Dropdown */}
                    {isProfileOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-0"
                                onClick={() => setIsProfileOpen(false)}
                            ></div>
                            <div className="absolute top-full mt-4 right-0 w-72 bg-surface border border-border-main rounded-[2.5rem] shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-200 p-5 overflow-hidden">
                                <div className="px-3 py-4 mb-4">
                                    <p className="text-sm font-bold text-foreground leading-tight">{roleInfo.name}</p>
                                    <p className="text-[11px] text-muted font-medium mt-1 truncate">{roleInfo.name.toLowerCase().replace(' ', '.')}@paud.sch.id</p>
                                </div>
                                <div className="h-px bg-border-main/50 mb-4 mx-2"></div>
                                <div className="space-y-1">
                                    <Link
                                        href="/sekolah/profil"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-secondary hover:bg-primary-soft hover:text-primary rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <User className="w-5 h-5 opacity-50 group-hover/item:opacity-100 transition-opacity" /> Profil Saya
                                    </Link>
                                    <Link
                                        href="/sekolah/settings"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-secondary hover:bg-primary-soft hover:text-primary rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <Settings className="w-5 h-5 opacity-50 group-hover/item:opacity-100 transition-opacity" /> Pengaturan
                                    </Link>
                                    <div className="h-px bg-border-main/50 my-4 mx-2"></div>
                                    <Link
                                        href="/login"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-error hover:bg-error-soft rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <LogOut className="w-5 h-5 opacity-70 group-hover/item:opacity-100 transition-opacity" /> Keluar
                                    </Link>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
